import React from 'react'
import styles from './MicrositeCategoriesSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeImageUploader from '../../../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// A store with no saved categories-section content yet still gets
// sensible empty defaults to start editing from.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    subtitle: '',
    description: '',
    categories: []
}

const EMPTY_CATEGORY = { title: '', image: '', description: '' }

// Backfills any missing top-level key from DEFAULT_CONTENT so older
// or partially-saved content still renders without crashing.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    categories: content?.categories || []
})

/**
 * MicrositeCategoriesSectionForm
 * Content editor for the landing page "Categories" section,
 * matching the real production content shape: title/subtitle/
 * description plus a repeatable list of category cards, each with
 * a title, image, and short description (no icon or link field —
 * those don't exist in the real shape and were dropped here).
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeCategoriesSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Categories</h2>
            <p className={styles.groupDesc}>Showcase the service categories your salon offers.</p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Our Categories"
                maxLength={80}
                showCharCount
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. BROWSE CATEGORIES"
                maxLength={60}
                showCharCount
            />
            <TextInput
                label="Description"
                value={safe.description}
                onChange={(v) => update('description', v)}
                placeholder="e.g. Explore our wide range of categories tailored to your beauty needs."
                maxLength={220}
                showCharCount
            />

            <label className={styles.rowsLabel}>Category cards</label>
            <MicrositeRepeatableRows
                items={safe.categories}
                onChange={(v) => update('categories', v)}
                emptyItem={{ ...EMPTY_CATEGORY }}
                addLabel="Add category"
                renderSummary={(item) => item.title || 'New category'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.categoryFields}>
                        <TextInput
                            label="Title"
                            value={item.title}
                            onChange={(v) => updateItem('title', v)}
                            placeholder="e.g. Hair"
                        />
                        <MicrositeImageUploader
                            label="Image"
                            value={item.image}
                            onChange={(v) => updateItem('image', v)}
                            placeholder="https://example.com/category.jpg"
                        />
                        <TextInput
                            label="Description"
                            value={item.description}
                            onChange={(v) => updateItem('description', v)}
                            placeholder="e.g. Professional hair styling, cutting, and coloring services."
                        />
                    </div>
                )}
            />
        </section>
    )
}

export default MicrositeCategoriesSectionForm